<template>
  <v-container :class="containerClass" fluid>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="itemList.data"
          :loading="isLoading"
          :options.sync="options"
          :server-items-length="itemList.meta.total"
          :footer-props="defaultDataTableFooterProps"
          item-key="id"
        >
          <template #top>
            <v-sheet>
              <v-container fluid class="pa-4">
                <v-row align="center">
                  <v-col md="6" cols="12">
                    <h2>Payroll List</h2>
                  </v-col>
                  <v-col md="6" cols="12" class="text-right">
                    <v-btn
                      v-if="hasPermission('payrolls.create')"
                      color="primary"
                      class="mx-1"
                      :small="$vuetify.breakpoint.mobile"
                      :href="$route('payrolls.create')"
                    >
                      <v-icon :small="$vuetify.breakpoint.mobile" left
                        >mdi-plus</v-icon
                      >Tambah
                    </v-btn>
                    <v-btn
                      class="mx-1"
                      :small="$vuetify.breakpoint.mobile"
                      @click="showFilters = !showFilters"
                    >
                      <v-icon :small="$vuetify.breakpoint.mobile" left
                        >mdi-filter</v-icon
                      >{{ showFilters ? 'Hide' : 'Show' }} Filter
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-sheet>
            <v-divider></v-divider>
            <v-expand-transition>
              <v-sheet v-show="showFilters" color="grey lighten-5">
                <v-container fluid class="pa-4">
                  <v-row>
                    <v-col v-if="!isTeacherAccount" md="2" cols="12">
                      <div class="label mb-2">Teacher</div>
                      <v-autocomplete
                        v-model="filters.teacher_id"
                        :items="teachers"
                        item-text="name"
                        item-value="id"
                        :loading="isTeacherListLoading"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Teacher"
                        hide-details
                        dense
                        clearable
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Status</div>
                      <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Status"
                        hide-details
                        dense
                        clearable
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Period</div>
                      <v-menu
                        ref="periodMenu"
                        v-model="periodMenu"
                        transition="scale-transition"
                        offset-y
                        max-width="300px"
                        min-width="auto"
                      >
                        <template #activator="{ on, attrs }">
                          <v-text-field
                            v-model="period"
                            label="Period"
                            prepend-icon="mdi-calendar"
                            readonly
                            outlined
                            solo
                            flat
                            background-color="white"
                            hide-details
                            dense
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="period"
                          type="month"
                          scrollable
                          @change="changePeriod"
                        >
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Paid Date</div>
                      <date-picker
                        v-model="filters.paid_date"
                        value-type="format"
                        type="date"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Created At</div>
                      <date-picker
                        v-model="filters.created_at"
                        value-type="format"
                        type="datetime"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Updated At</div>
                      <date-picker
                        v-model="filters.updated_at"
                        value-type="format"
                        type="datetime"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                  </v-row>
                </v-container>
                <v-divider></v-divider>
              </v-sheet>
            </v-expand-transition>
          </template>
          <template #[`item.code`]="{ item }">
            <strong>{{ item.code }}</strong>
          </template>
          <template #[`item.teacher`]="{ item }">
            {{ item.teacher.name }}
          </template>
          <template #[`item.period`]="{ item }">
            {{
              $moment({ year: item.year, month: item.month - 1 }).format(
                'MMM YYYY'
              )
            }}
          </template>
          <template #[`item.total_hourly_amount`]="{ item }">
            {{
              `${item.currency_code} ${formatNumber(item.total_hourly_amount)}`
            }}
          </template>
          <template #[`item.total_converted_hourly_amount`]="{ item }">
            <div v-if="item.currency_code != 'IDR'">
              {{
                `IDR ${formatNumber(
                  item.total_hourly_amount * item.currency_exchange_rate
                )}`
              }}
            </div>
            <div v-else>
              {{ `IDR ${formatNumber(item.total_hourly_amount)}` }}
            </div>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip
              :color="payrollStatusProps(item.status, 'color')"
              small
              dark
            >
              <strong>
                {{ payrollStatusProps(item.status, 'text') }}
              </strong>
            </v-chip>
          </template>
          <template #[`item.paid_date`]="{ item }">
            {{ formatDate(item.paid_date, 'DD MMM YYYY') }}
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at, 'YYYY-MM-DD HH:mm:ss') }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at, 'YYYY-MM-DD HH:mm:ss') }}
          </template>
          <template #[`item.action`]="{ item }">
            <div class="text-right">
              <v-btn
                v-if="hasPermission('payrolls.view')"
                small
                color="info"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                :href="$route('payrolls.download-pdf', item.id)"
              >
                <v-icon small>mdi-cloud-download</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;Download</span>
              </v-btn>
              <v-btn
                v-if="hasPermission('payrolls.view')"
                small
                color="secondary"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                :href="$route('payrolls.detail', item.id)"
              >
                <v-icon small>mdi-eye</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;View</span>
              </v-btn>
              <v-btn
                v-if="hasPermission('payrolls.update')"
                small
                color="warning"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                :href="$route('payrolls.update', item.id)"
              >
                <v-icon small>mdi-pencil</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;Edit</span>
              </v-btn>
              <v-btn
                v-if="hasPermission('payrolls.delete')"
                small
                color="error"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                @click="showDeleteDialog(item)"
              >
                <v-icon small>mdi-delete-forever</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;Delete</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <payroll-delete-dialog
      v-if="deleteDialog.show"
      v-model="deleteDialog.show"
      :payroll="deleteDialog.item"
      @delete="getListData"
    >
    </payroll-delete-dialog>
  </v-container>
</template>
<script>
import LayoutDashboard from '@layouts/Dashboard'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import constant from '@/constant'
import PayrollDeleteDialog from '@components/Payroll/DeleteDialog'
import { ListParams } from '@/models'
import utils from '@/utils'

export default {
  name: 'Payroll',
  components: {
    PayrollDeleteDialog,
  },
  layout: LayoutDashboard,
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      showFilters: true,
      periodMenu: false,
      period: null,
      filters: {
        teacher_id: null,
        year: null,
        month: null,
        status: null,
        paid_date: [],
        created_at: [],
        updated_at: [],
      },
      deleteDialog: {
        show: false,
        item: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      itemList: 'payroll/list',
      isLoading: 'payroll/isListLoading',
      teacherList: 'teacher/list',
      isTeacherListLoading: 'teacher/isListLoading',
      listParams: 'payroll/listParams',
    }),
    statusOptions() {
      return constant.payrollStatusOptions
    },
    teachers() {
      return this.teacherList.data
    },
    headers() {
      return [
        { text: 'ID', value: 'code', sortable: false },
        ...(this.isTeacherAccount === false
          ? [{ text: 'Teacher', value: 'teacher', sortable: false }]
          : []),
        { text: 'Period', value: 'period' },
        {
          text: 'Total Amount',
          value: 'total_hourly_amount',
          sortable: false,
        },
        ...(this.isEmployeeAccount
          ? [
              {
                text: 'Total Converted Amount',
                value: 'total_converted_hourly_amount',
                sortable: false,
              },
            ]
          : []),
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Paid Date', value: 'paid_date' },
        ...(this.isEmployeeAccount
          ? [
              { text: 'Created At', value: 'created_at' },
              { text: 'Updated At', value: 'updated_at' },
            ]
          : []),
        { text: 'Action', value: 'action', sortable: false, align: 'center' },
      ]
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getListData()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getItemList: 'payroll/getList',
      getTeacherList: 'teacher/getList',
    }),
    ...mapMutations({
      setListParams: 'payroll/setListParams',
    }),
    init() {
      if (this.isTeacherAccount) {
        this.filters.teacher_id = this.authUser.account_id
      } else {
        this.getTeacherList()
      }
      if (this.listParams) {
        _.merge(this.filters, this.listParams.filters)
        _.merge(this.options, this.listParams.options)
      }
    },
    changePeriod(value) {
      this.$refs.periodMenu.save(value)
      if (value) {
        const dt = moment(value, 'YYYY-MM')
        this.filters.year = dt.year()
        this.filters.month = dt.month()
      } else {
        this.filters.year = null
        this.filters.month = null
      }
      this.getListData()
    },
    getListData() {
      const listParams = new ListParams(this.filters, this.options)
      this.getItemList(utils.toServerSideQuery(listParams))
      this.setListParams(listParams)
    },
    showDeleteDialog(item) {
      this.deleteDialog.item = item
      this.deleteDialog.show = true
    },
  },
}
</script>
